import { PhoneIcon } from 'assets/icons';
import Button from 'components/button';
import ConfirmDialog from 'components/record/ConfirmDialog';
import { apiErrorText } from 'components/record/recordErrors';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import FieldLabel from 'components/styledInput/FieldLabel';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import AppointmentForm from 'components/planning/AppointmentForm';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import { recruitersOf } from 'components/companies/companyAssignees';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import ChoiceChips from 'components/tasks/ChoiceChips';
import TaskLinksField from 'components/tasks/form/TaskLinksField';
import { fr } from 'date-fns/locale';
import { ADMIN_ROLE_LABELS } from 'helper/roles';
import { isRealId, linkOptionsOf } from 'helper/taskLinks';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

registerLocale('fr-FR', fr);

const CALL_RESULTS = ['answered', 'callback', 'no_answer', 'not_interested'];
const LOST_REASONS = ['price', 'competitor', 'noNeed', 'direct', 'unreachable', 'closed'];
const isMail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// A link is known by its kind and id, a contact by its company too: the same
// person may belong to two.
const linkOf = (options, type, id, companyId = null) =>
  options.find(
    (option) => option.type === type && option.id === id && (type !== 'contact' || (option.companyId ?? null) === companyId),
  ) ?? null;

// What the api reads of a link: its kind, its id and a contact's company.
const noteLinkPayload = (link) => ({
  type: link.type,
  id: link.id,
  ...(link.type === 'contact' && link.companyId ? { companyId: link.companyId } : {}),
});

// The records a note opens on: its own (an edit), those given (a call), else
// the record it is opened from (a contact, a company or a group). A record
// missing from the options keeps the page's name, so that it is never dropped.
const noteLinksOf = (data, options) => {
  if (data.note) return data.note.links ?? [];
  if (data.links?.length) return data.links;
  const name = data.recordName ?? '';
  const group = data.groupId ? (linkOf(options, 'group', data.groupId) ?? { type: 'group', id: data.groupId, label: name }) : null;
  if (data.contact) {
    const { id, name: contactName = '', companyId = null } = data.contact;
    // A member of a company has an account; a group's contact or a HubSpot
    // one is known by name only.
    const contact =
      companyId && isRealId(id)
        ? (linkOf(options, 'contact', id, companyId) ?? { type: 'contact', id, label: contactName, sub: name, companyId })
        : { type: 'contact', id: `name:${contactName}`, label: contactName, sub: companyId ? name : '', companyId };
    return group ? [group, contact] : [contact];
  }
  if (data.companyId) return [linkOf(options, 'company', data.companyId) ?? { type: 'company', id: data.companyId, label: name }];
  return group ? [group] : [];
};

// What a form holds when it opens: the contact it was opened for (or the
// first one), a follow-up in three months.
const initialForm = (data, now = new Date()) => {
  const contacts = data.contacts ?? [];
  const contact = contacts.find((one) => one.name === data.contactName) ?? contacts[0] ?? null;
  return {
    body: data.note?.body ?? '',
    links: data.kind === 'note' ? noteLinksOf(data, []) : [],
    linksTouched: false,
    contactName: contact?.name ?? '',
    result: 'answered',
    note: '',
    reason: 'price',
    details: '',
    groupName: data.recordName ?? '',
    recruiterId: data.recruiterId ?? '',
    relaunch: isoDay(addDays(now, 90)),
    noRelaunch: false,
    firstname: data.contact?.firstname ?? '',
    lastname: data.contact?.lastname ?? '',
    position: data.contact?.position ?? '',
    phone: data.contact?.phone ?? '',
    email: data.contact?.email ?? '',
    search: '',
  };
};

/**
 * The forms of a record page, in the slide-over (`objID`: { kind, companyId
 * | groupId, recordName, contacts, contactName, contact }). A note's `objID`
 * may also carry `contact` ({ id, name, companyId }, the contact page),
 * `callId` and `links` (a call), or `note` (the note edited): a note, a call
 * made, an appointment, a company lost, a contact of a group, a
 * company joining a group, a company made a group. Each writes through server/crm/crmApi and tells
 * the page (useRecordChanged).
 */
const RecordFormPanel = () => {
  const { objID } = UseContext();
  const data = objID && typeof objID === 'object' && objID.kind ? objID : null;
  const { text } = useText('recordForm');
  const { call } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const navigate = useNavigate();
  const notify = useNotifyRecordChanged();
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const contacts = data?.contacts ?? [];
  const only = contacts.length === 1 ? contacts[0] : null;
  const target = data?.groupId ? { groupId: data.groupId } : { companyId: data?.companyId };

  const [form, setForm] = useState({});
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  // A new form at every opening.
  useEffect(() => {
    if (objID && typeof objID === 'object' && objID.kind) setForm(initialForm(objID));
    setConfirmingDelete(false);
  }, [objID]);

  // The companies that may join the group, as the search narrows them.
  const [candidates, setCandidates] = useState(null);
  useEffect(() => {
    if (data?.kind !== 'groupCompany') return undefined;
    const timer = setTimeout(
      () => call(crmApi.listGroupCandidates, { search: form.search ?? '' }).then(setCandidates, () => setCandidates([])),
      250,
    );
    return () => clearTimeout(timer);
  }, [call, data?.kind, form.search]);

  // The team, for « Passer en ancien client »: the recruiters a new client may
  // be given as it becomes an old one, and its commercial, who stays.
  const [team, setTeam] = useState(null);
  useEffect(() => {
    if (data?.kind !== 'toOld') return;
    call(crmApi.getTeam).then((answer) => setTeam(answer ?? []), () => setTeam([]));
  }, [call, data?.kind]);
  const recruiters = team && recruitersOf(team);
  const personName = (person) => `${person.firstname ?? ''} ${person.lastname ?? ''}`.trim();
  const commercial = team?.find((person) => person.id === data?.commercialId);
  const recruiter = recruiters?.find((person) => person.id === form.recruiterId);

  // The records a note may be linked to (no temp worker): the default links
  // take their labels from them, unless the user already changed the links.
  const [linkOptions, setLinkOptions] = useState([]);
  useEffect(() => {
    if (!objID || typeof objID !== 'object' || objID.kind !== 'note') return undefined;
    let current = true;
    setLinkOptions([]);
    call(crmApi.getContacts).then(
      (records) => {
        if (!current) return;
        const options = linkOptionsOf(records ?? []);
        setLinkOptions(options);
        setForm((shown) => (shown.linksTouched ? shown : { ...shown, links: noteLinksOf(objID, options) }));
      },
      // Without them, the default links stay and the search finds nothing.
      () => {},
    );
    return () => {
      current = false;
    };
  }, [call, objID]);

  if (!data) return null;
  const close = () => toggleSlideOver();
  // The result of `run`, or null when it failed (the error is toasted, as
  // `errorText` words it when given).
  const submit = async (run, message, errorText) => {
    setBusy(true);
    try {
      const result = await run();
      toast.success(message);
      notify();
      close();
      return result ?? true;
    } catch (error) {
      toast.error(errorText ? errorText(error) : error.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const contactErrorText = (error) => apiErrorText(text, 'contactErrors', error);
  const noteErrorText = (error) => apiErrorText(text, 'noteErrors', error);
  const editedNote = data.kind === 'note' ? data.note ?? null : null;
  const noteFields = () => ({ body: form.body.trim(), links: (form.links ?? []).map(noteLinkPayload) });
  const contactOptions = contacts.map((contact) => ({ value: contact.name, label: contact.name }));
  const contact = contacts.find((one) => one.name === form.contactName);

  const FORMS = {
    note: {
      title: text(editedNote ? 'noteEditTitle' : 'noteTitle'),
      hint: text('noteHint'),
      cta: text(editedNote ? 'noteEditCta' : 'noteCta'),
      valid: Boolean(form.body?.trim()) && form.links?.length > 0,
      save: () =>
        editedNote
          ? submit(() => call(crmApi.updateRecordNote, { id: editedNote.id, ...noteFields() }), text('noteUpdated'), noteErrorText)
          : submit(
              () => call(crmApi.addRecordNote, { ...noteFields(), ...(data.callId ? { callId: data.callId } : {}) }),
              text('noteSaved'),
              noteErrorText,
            ),
      body: (
        <>
          <StyledInput
            type="textarea"
            label={text('noteLabel')}
            placeholder={text('notePlaceholder')}
            value={form.body ?? ''}
            onChange={(event) => set('body', event.target.value)}
          />
          <TaskLinksField
            label={text('noteLinksLabel')}
            placeholder={text('noteLinksPlaceholder')}
            hint={text('noteLinksHint')}
            options={linkOptions}
            value={form.links ?? []}
            onChange={(links) => setForm((current) => ({ ...current, links, linksTouched: true }))}
            withTempWorkers={false}
          />
        </>
      ),
    },
    call: {
      title: text('callTitle', { name: only?.name ?? data.recordName }),
      hint: only ? text('callHintOne', { company: data.recordName }) : text('callHintMany'),
      cta: text('callCta'),
      valid: true,
      save: () =>
        submit(() => call(crmApi.logRecordCall, { ...target, contactName: form.contactName, result: form.result, note: form.note }), text('callSaved')),
      body: (
        <>
          {contacts.length > 1 && (
            <StyledInput type="select" label={text('who')} list={contactOptions} value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          )}
          {contact?.phone && (
            <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:underline">
              <PhoneIcon wh={16} color="currentColor" />
              {contact.phone}
            </a>
          )}
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('resultLabel')} />
            <ChoiceChips options={CALL_RESULTS.map((result) => [result, text(`result_${result}`)])} value={form.result} onPick={(result) => set('result', result)} />
          </div>
          <StyledInput type="textarea" label={text('callNote')} placeholder={text('callNotePlaceholder')} value={form.note ?? ''} onChange={(event) => set('note', event.target.value)} />
        </>
      ),
    },
    // The company leaves the list: back to the list, where it is gone.
    // A group of the company's name, the company in it: its page opens.
    toGroup: {
      title: text('toGroupTitle'),
      cta: text('toGroupCta'),
      valid: Boolean(form.groupName?.trim()),
      save: () =>
        submit(
          () => call(crmApi.makeCompanyGroup, { companyId: data.companyId, name: form.groupName.trim() }),
          text('toGroupSaved'),
        ).then((result) => result?.groupId && navigate(`/company/group/${result.groupId}`)),
      body: (
        <>
          <StyledInput
            type="text"
            label={text('groupName')}
            value={form.groupName ?? ''}
            onChange={(event) => set('groupName', event.target.value)}
          />
          <p className="text-sm text-gray-600">{text('toGroupHint')}</p>
        </>
      ),
    },
    // « Passer en ancien client »: an old client is followed by its recruiter
    // with its commercial, so the recruiter is chosen on the way (JBWEB-1423).
    toOld: {
      title: text('toOldTitle'),
      cta: text('toOldCta'),
      valid: Boolean(form.recruiterId),
      blocker: text('toOldBlocker'),
      save: () =>
        submit(
          () => call(crmApi.moveCompanyToOld, { companyId: data.companyId, recruiterId: form.recruiterId }),
          text('toOldSaved'),
          (error) => apiErrorText(text, 'toOldErrors', error),
        ),
      body: (
        <>
          <p className="text-sm text-gray-700">{text('toOldIntro')}</p>
          {/* Who follows the company once it is an old client: the commercial
              stays, the recruiter is the one chosen below. */}
          <dl className="divide-y divide-gray-100 rounded-lg bg-gray-50 text-sm">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <dt className="text-gray-500">{text('toOldCommercial')}</dt>
              <dd className="text-right text-gray-900">
                {commercial ? (
                  <>
                    {personName(commercial)}
                    <span className="text-gray-500"> · {text('toOldStays')}</span>
                  </>
                ) : (
                  <span className="text-gray-500">{text('toOldNobody')}</span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <dt className="text-gray-500">{text('recruiter')}</dt>
              <dd className={`text-right ${recruiter ? 'text-gray-900' : 'font-medium text-yellow-700'}`}>
                {recruiter ? personName(recruiter) : text('toOldToChoose')}
              </dd>
            </div>
          </dl>
          {/* The list of the company app's « Postes à risques concernés »
              (RiskyPositionTemplate): one framed box, a line between rows. */}
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('toOldPick')} />
            {recruiters ? (
              <div
                role="radiogroup"
                aria-label={text('recruiter')}
                className="min-w-0 overflow-hidden rounded-lg border border-gray-100 bg-white"
              >
                <ul className="divide-y divide-gray-100">
                  {recruiters.map((person) => (
                    <li key={person.id} className="flex px-5 py-4 hover:bg-gray-50">
                      <StyledInput
                        type="radio"
                        name="toOld-recruiter"
                        label={
                          <span className="flex min-w-0 items-center gap-2.5">
                            <EmployeeAvatar employee={person} />
                            <span className="flex min-w-0 flex-col">
                              <span className="break-words">{personName(person)}</span>
                              <span className="text-xs font-normal text-gray-500">{ADMIN_ROLE_LABELS[person.role]}</span>
                            </span>
                          </span>
                        }
                        checked={form.recruiterId === person.id}
                        onChange={() => set('recruiterId', person.id)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              // Rows of the same height while the team is read: no jump.
              <div aria-busy="true" className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="flex items-center gap-3 px-5 py-4">
                    <span className="size-4 shrink-0 animate-pulse rounded-full bg-gray-200" />
                    <span className="size-6 shrink-0 animate-pulse rounded-full bg-gray-200" />
                    <SkeletonLine className="w-40" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ),
    },
    lost: {
      title: text('lostTitle'),
      cta: text('lostCta'),
      valid: true,
      save: () =>
        submit(
          () =>
            call(crmApi.setCompanyStage, {
              companyId: data.companyId,
              stage: 'lost',
              lostReason: [text(`reason_${form.reason}`), form.details?.trim()].filter(Boolean).join(' · '),
              relaunchOn: form.noRelaunch ? null : form.relaunch,
            }),
          text('lostSaved'),
        ),
      body: (
        <>
          <StyledInput
            type="select"
            label={text('reason')}
            list={LOST_REASONS.map((reason) => ({ value: reason, label: text(`reason_${reason}`) }))}
            value={form.reason}
            onChange={(event) => set('reason', event.target.value)}
          />
          <StyledInput type="textarea" label={text('reasonDetails')} value={form.details ?? ''} onChange={(event) => set('details', event.target.value)} />
          {!form.noRelaunch && (
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={form.relaunch ? fromIsoDay(form.relaunch) : null}
              onChange={(date) => date && set('relaunch', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('relaunch')} />}
            />
          )}
          <StyledInput type="checkbox" label={text('noRelaunch')} checked={Boolean(form.noRelaunch)} onChange={() => set('noRelaunch', !form.noRelaunch)} />
        </>
      ),
    },
    groupContact: {
      title: text(data.contact ? 'contactEditTitle' : 'contactAddTitle'),
      cta: text('contactCta'),
      valid: Boolean(form.firstname?.trim() || form.lastname?.trim()) && (!form.email?.trim() || isMail(form.email)),
      save: () =>
        submit(
          () =>
            call(crmApi.saveGroupContact, {
              groupId: data.groupId,
              contact: {
                ...(data.contact?.id && { id: data.contact.id }),
                firstname: form.firstname.trim(),
                lastname: form.lastname.trim().toUpperCase(),
                position: form.position.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
              },
            }),
          text('contactSaved'),
          contactErrorText,
        ),
      // An existing contact is deleted from its form, once confirmed.
      remove: data.contact?.id && {
        label: text('contactDelete'),
        title: text('contactDeleteTitle', {
          name: [data.contact.firstname, data.contact.lastname].filter(Boolean).join(' '),
        }),
        cancel: text('contactDeleteCancel'),
        confirm: text('contactDeleteConfirm'),
        run: async () => {
          const done = await submit(
            () => call(crmApi.deleteGroupContact, { groupId: data.groupId, contactId: data.contact.id }),
            text('contactDeleted'),
            contactErrorText,
          );
          setConfirmingDelete(false);
          // The contact's own page is gone: back to its group.
          if (done && data.afterDelete) navigate(data.afterDelete);
        },
      },
      body: (
        <div className="grid grid-cols-2 gap-3">
          <StyledInput type="text" label={text('firstname')} value={form.firstname ?? ''} onChange={(event) => set('firstname', event.target.value)} />
          <StyledInput type="text" label={text('lastname')} value={form.lastname ?? ''} onChange={(event) => set('lastname', event.target.value)} />
          <StyledInput type="text" label={text('position')} value={form.position ?? ''} onChange={(event) => set('position', event.target.value)} />
          <StyledInput type="text" label={text('phone')} value={form.phone ?? ''} onChange={(event) => set('phone', event.target.value)} />
          <div className="col-span-2">
            <StyledInput type="email" label={text('email')} value={form.email ?? ''} onChange={(event) => set('email', event.target.value)} />
          </div>
        </div>
      ),
    },
    groupCompany: {
      title: text('groupCompanyTitle'),
      body: (
        <>
          <StyledInput type="text" label={text('searchCompany')} value={form.search ?? ''} onChange={(event) => set('search', event.target.value)} />
          <div className="flex flex-col">
            {(candidates ?? []).map((company) => (
              <div key={company.id} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium uppercase text-gray-900">{company.name}</p>
                  <p className="truncate text-xs text-gray-600">{company.department}</p>
                </div>
                <Button
                  color="SECONDARY"
                  size="SM"
                  disabled={busy}
                  onClick={() =>
                    submit(
                      () => call(crmApi.addCompanyToGroup, { companyId: company.id, groupId: data.groupId }),
                      text('groupCompanySaved', { company: company.name, group: data.recordName }),
                    )
                  }
                >
                  {text('groupCompanyCta')}
                </Button>
              </div>
            ))}
            {candidates && !candidates.length && <p className="py-4 text-sm text-gray-600">{text('groupCompanyNone')}</p>}
          </div>
        </>
      ),
    },
  };
  // An appointment: the one form of the planning, linked to the record, the
  // week it falls in beside it.
  if (data.kind === 'event') {
    const tomorrow = isoDay(addDays(new Date(), 1));
    return (
      <AppointmentForm
        key={JSON.stringify([data.companyId, data.groupId, data.contactName])}
        mode="create"
        layout="panel"
        record={{ companyId: data.companyId ?? null, name: data.recordName ?? '', contacts, contactName: data.contactName ?? '' }}
        initial={{ startDay: tomorrow, endDay: tomorrow, start: '10:00', end: '11:00' }}
        header={
          <SlideOverHeader hide={close} closeLabel={text('close')}>
            <span className="flex flex-col">
              {text('eventTitle')}
              <span className="text-sm font-normal text-gray-600">{data.recordName}</span>
            </span>
          </SlideOverHeader>
        }
        onSaved={() => {
          notify();
          close();
        }}
        onCancel={close}
      />
    );
  }
  const current = FORMS[data.kind];
  if (!current) return null;

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        <span className="flex flex-col">
          {current.title}
          <span className="text-sm font-normal text-gray-600">{current.hint ?? data.recordName}</span>
        </span>
      </SlideOverHeader>
      <SlideOverBody>
        <div className="flex flex-col gap-4">{current.body}</div>
      </SlideOverBody>
      {current.save && (
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            {current.blocker && !current.valid && <span className="mr-auto text-xs text-gray-500">{current.blocker}</span>}
            {current.remove && (
              <Button color="SECONDARY" size="SM" disabled={busy} onClick={() => setConfirmingDelete(true)}>
                {current.remove.label}
              </Button>
            )}
            <Button color="SECONDARY" size="SM" onClick={close}>
              {text('cancel')}
            </Button>
            <Button color="PRIMARY" size="SM" disabled={!current.valid || busy} onClick={current.save}>
              {current.cta}
            </Button>
          </SlideOverFooter>
        </div>
      )}
      {current.remove && (
        <ConfirmDialog
          open={confirmingDelete}
          busy={busy}
          title={current.remove.title}
          cancelLabel={current.remove.cancel}
          confirmLabel={current.remove.confirm}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={current.remove.run}
        />
      )}
    </>
  );
};

export default RecordFormPanel;
